import {Injectable, inject} from '@angular/core';
import {Store} from '@ngrx/store';
import {Observable, combineLatest} from 'rxjs';
import {share, switchMap} from 'rxjs/operators';
import {loadSubstitutes, reloadSubstitutes, deleteSubstitute} from '../../reducers/substitute/substitute.actions';
import {
  selectSubstitutes,
  selectSubstitutesLoaded,
  selectSubstitutesLoading,
  selectSubstitutesScope
} from '../../reducers/substitute/substitute.selectors';
import {Substitute, CreteSubstitutePayload} from '../models/substitute.model';
import {ErrorHandlingHttpClient} from './error-handling-http-client.service';
import {API} from '../constants/api.constants';
import type {SubstituteScope} from '../../reducers/substitute/substitute.reducer';

@Injectable({
  providedIn: 'root'
})
export class SubstituteService {
  private store = inject(Store);
  constructor(private errorHandlingHttpClient: ErrorHandlingHttpClient) {}

  load$(scope: SubstituteScope = 'my'): Observable<Substitute[]> {
    return combineLatest([this.store.select(selectSubstitutesLoaded), this.store.select(selectSubstitutesScope)]).pipe(
      switchMap(([loaded, oldScope]) => {
        if (!loaded || oldScope !== scope) {
          this.store.dispatch(loadSubstitutes({scope}));
        }

        return this.store.select(selectSubstitutes);
      }),
      share()
    );
  }

  select$(): Observable<Substitute[]> {
    return this.store.select(selectSubstitutes);
  }

  loading$(): Observable<boolean> {
    return this.store.select(selectSubstitutesLoading);
  }

  reload(): void {
    this.store.dispatch(reloadSubstitutes());
  }

  add$(scope: SubstituteScope, userName: string, substituteUserName: string, substitute: CreteSubstitutePayload): Observable<Substitute> {
    if (scope === 'all') {
      return this.errorHandlingHttpClient.post(API.adminSubstitute(userName, substituteUserName), substitute, undefined, {
        duration: 0
      });
    }
    return this.errorHandlingHttpClient.post(API.substitute(substituteUserName), substitute, undefined, {
      duration: 0
    });
  }

  edit$(scope: SubstituteScope, userName: string, substituteUserName: string, substitute: CreteSubstitutePayload): Observable<Substitute> {
    if (scope === 'all') {
      return this.errorHandlingHttpClient.put(API.adminSubstitute(userName, substituteUserName), substitute);
    }

    return this.errorHandlingHttpClient.put(API.substitute(substituteUserName), substitute);
  }

  remove(scope: SubstituteScope, userName: string, substituteUserName: string): void {
    this.store.dispatch(deleteSubstitute({substituteUserName, userName, scope}));
  }
}
